/**
 * What the store writes down, and what it must never write down.
 *
 * ## Append-only, enforced by the database
 *
 * `store_order_events` carries a trigger that refuses UPDATE and DELETE, the
 * same way `gacha_events`, `market_events`, `inventory_events`, `trade_events`
 * and `loadout_changes` do. A payment trail that can be edited afterwards is
 * exactly the record a dispute would be about.
 *
 * ## What never enters an event
 *
 * No access token, no webhook secret, no QR payload, no Pix copia-e-cola, no
 * CPF, no full e-mail, and no raw provider body. A payload is convenient right
 * up to the day it is subpoenaed or leaked; the identifiers below are enough to
 * reconstruct what happened by asking the provider again, which is the only
 * source that was ever authoritative anyway.
 *
 * `redactEventDetail` is the choke point. Everything written to an event goes
 * through it.
 */

export const STORE_EVENT_KINDS = [
  'order_created',
  /** The charge was handed to the provider. */
  'provider_charge_requested',
  /** The provider answered with a QR. Its CONTENTS are never stored. */
  'provider_charge_created',
  /** A notification arrived. Its body is never stored. */
  'provider_notification_received',
  /** We asked the provider what it thinks, rather than trusting the message. */
  'provider_status_queried',
  'payment_approved',
  'payment_rejected',
  'order_expired',
  'order_cancelled',
  /** The Cash or the VIP actually landed in the account. */
  'benefit_granted',
  /** A replay: the same key, the same answer, nothing moved. */
  'idempotent_replay',
  /** Something did not add up - amount, currency, reference or recipient. */
  'divergence_detected',
  'refund_requested',
  'refund_confirmed',
  /** Cash could not be taken back because it was already spent. */
  'cash_debt_recorded',
  'cash_debt_settled',
  'manual_review_flagged',
  /**
   * A human resolved a review, and the resolution was re-verified first.
   *
   * Distinct from `payment_approved` on purpose: this one says a PERSON decided,
   * after the provider was asked again and every guarantee agreed. An audit that
   * cannot tell an operator's decision from an automated one is an audit that
   * cannot answer the only question a dispute asks.
   */
  'manual_review_resolved',
] as const;

export type StoreEventKind = (typeof STORE_EVENT_KINDS)[number];

/**
 * The only keys an event detail may carry.
 *
 * An allow-list, not a deny-list. A deny-list has to anticipate every field a
 * provider might add to its payload next quarter; an allow-list keeps what we
 * decided to keep and drops the rest, including fields nobody has seen yet.
 */
export const STORE_EVENT_DETAIL_KEYS = [
  'fromState',
  'toState',
  'provider',
  'providerEnvironment',
  /** The provider's opaque order/preference id. Not a secret, not a payload. */
  'providerOrderId',
  /** The provider's opaque payment id. The thing we re-query with. */
  'providerPaymentId',
  /** OUR reference, which we generated. Safe by construction. */
  'externalReference',
  'amountCents',
  'currency',
  'productId',
  'productVersion',
  'cashAmount',
  'vipSeconds',
  'reason',
  'expectedAmountCents',
  'observedAmountCents',
  /*
   * CURRENCY, and only currency. These were once used for a COUNTRY
   * comparison, which put `expectedCurrency: "BRL"` beside
   * `observedCurrency: "BRA"` in the trail - two different standards under one
   * name, and a reader with no way to tell. Events already written keep those
   * names, because the audit is append-only and rewriting history to look
   * correct is worse than history that reads oddly.
   */
  'expectedCurrency',
  'observedCurrency',
  /** COUNTRY, in ISO 3166-1 alpha-3. Never a currency. */
  'expectedCountryCode',
  'observedCountryCode',
  'providerStatus',
  /** The raw `status_detail` behind it. Audit only; never a decision. */
  'providerStatusDetail',
  /** 'pix'. Which method actually settled, when it disagreed with the order. */
  'paymentMethodId',
  'purchaseId',
  'debtAmount',
  'notificationTopic',
  'signatureValid',
  'attempt',
  /**
   * The scripted sandbox scenario a charge was created under, when there was
   * one. A fixed enum decided by the server - never anything about the payer,
   * and absent from every production charge.
   */
  'sandboxScenario',
  /**
   * Why a provider call failed: 'network' | 'timeout' | 'http' | 'malformed' |
   * 'unsupported', and the HTTP status when there was one.
   *
   * Never the provider's message or body - those can carry an endpoint, a token
   * fragment or the payer's details. A kind and a status carry none of that and
   * are the whole difference between an outage and a payload we got wrong.
   */
  'providerFailureKind',
  'providerHttpStatus',
  /**
   * The provider's own short error code, when it named one.
   *
   * A slug, filtered by the adapter to `[a-z0-9_.-]{1,64}` - never free text
   * and never a field value. `409` alone cannot be acted on; `409` plus a code
   * can.
   */
  'providerErrorCode',
  /**
   * How many orders a search by our own reference returned.
   *
   * The count IS the decision: zero means nothing exists and charging again is
   * safe, one means reconcile it, more than one means a person. Writing it down
   * is what makes that decision auditable afterwards.
   */
  'resultCount',
  /**
   * SHA-256 of the body a charge was sent with.
   *
   * An anchor, not a payload: it proves two attempts were the same operation
   * without the audit ever holding the request. The idempotency key itself is
   * deliberately NOT here - it is the half of an idempotent request that
   * behaves like a credential.
   */
  'requestHash',
  /**
   * Which parts a notification carried. BOOLEANS only, never the values.
   *
   * A rejected signature is undiagnosable without them: three genuine
   * notifications were refused while the panel's own test passed, and the only
   * difference was one absent header. The values are the inputs to the check
   * being reported on, so they never appear - `hasRequestId: false` is the
   * whole story, and `x-request-id: abc123` would be evidence handed to
   * whoever reads the log.
   */
  'hasDataId',
  'hasRequestId',
  'hasSignatureTimestamp',
  /**
   * Diagnosis, development and test only. Booleans about the SHAPE of what
   * arrived - whether the body agreed with the query, and whether the signature
   * even looks like a hex SHA-256 - never about its contents.
   */
  'bodyAndQueryIdsMatch',
  'signatureLooksHex64',
] as const;

export type StoreEventDetailKey = (typeof STORE_EVENT_DETAIL_KEYS)[number];

/*
 * `| undefined` on purpose.
 *
 * The codebase compiles with `exactOptionalPropertyTypes`, so an optional key
 * and a key holding `undefined` are different types - and callers naturally
 * write `somethingOptional ?? undefined`. `redactEventDetail` drops undefined
 * before anything is persisted, so allowing it here costs nothing and saves a
 * conditional spread at every call site.
 */
export type StoreEventDetail = Partial<
  Record<StoreEventDetailKey, string | number | boolean | undefined>
>;

/**
 * Keys that must never be written, whatever anybody passes.
 *
 * Redundant with the allow-list above - and kept anyway, because it is the
 * thing a test can assert by name. "No token in the audit" is a claim; a list
 * of the names a token arrives under is a claim that can fail loudly.
 */
export const STORE_EVENT_FORBIDDEN_KEYS: readonly string[] = Object.freeze([
  'access_token',
  'accessToken',
  'authorization',
  'secret',
  'webhookSecret',
  'signature',
  'qr',
  'qrCode',
  'qr_code',
  'qrCodeBase64',
  'copiaECola',
  'pixCopiaECola',
  'cpf',
  'document',
  'email',
  'payer',
  'payerEmail',
  'body',
  'rawBody',
  'payload',
  'headers',
]);

/**
 * Everything written to an event passes through here.
 *
 * Unknown keys are dropped rather than rejected: an adapter that learns a new
 * provider field should not be able to fail a payment by mentioning it, and it
 * should not be able to persist it either. Values are coerced to primitives, so
 * a nested object carrying a payload cannot ride in on an allowed key.
 */
export function redactEventDetail(detail: Record<string, unknown> | undefined): StoreEventDetail {
  if (!detail) {
    return {};
  }
  const allowed = new Set<string>(STORE_EVENT_DETAIL_KEYS);
  const output: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(detail)) {
    if (!allowed.has(key) || value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      output[key] = value;
      continue;
    }
    if (typeof value === 'bigint') {
      output[key] = value.toString(10);
    }
    /*
     * Anything else - an object, an array, a function - is DROPPED on an
     * allowed key, not stringified. `JSON.stringify` would put the payload
     * straight back in under a name the allow-list permits.
     */
  }
  return output as StoreEventDetail;
}
